import { Program, web3 } from "@project-serum/anchor";
import * as anchor from "@project-serum/anchor";
import { Keypair, PublicKey } from "@solana/web3.js";
import {
  createAcceptOfferTx,
  createAddTreasuryTx,
  createCancelAuctionTx,
  createCancelOfferTx,
  createClaimAuctionTx,
  createCreateAuctionTx,
  createDelistNftTx,
  createDepositTx,
  createInitAuctionDataTx,
  createInitializeTx,
  createInitOfferDataTx,
  createInitSellDataTx,
  createInitUserTx,
  createListForSellNftTx,
  createMakeOfferTx,
  createPlaceBidTx,
  createPurchaseTx,
  createRemoveTreasuryTx,
  createSetPriceTx,
  createTransferFromVaultTx,
  createTransferTx,
  createUpdateFeeTx,
  createUpdateReserveTx,
  createWithdrawTx,
  getAllListedNFTs,
  getAllOffersForListedNFT,
  getAllStartedAuctions,
  getAuctionDataState,
  getGlobalState,
  getNFTPoolState,
  getOfferDataState,
  getUserPoolState,
} from "./lib/scripts";
import { isInitializedUser } from "./lib/utils";
import { NutMarketplace } from "./lib/nut_marketplace";
import { SOLANA_RPC } from "../config";
import {
  AUCTION_DATA_SEED,
  OFFER_DATA_SEED,
  SELL_DATA_SEED,
} from "./lib/types";

let solConnection = new web3.Connection(SOLANA_RPC);

// Address of the deployed program.
const MARKETPLACE_PROGRAM_ID = new PublicKey(
  "37vvcTUnSSstVDgzd1jhujmpbw3jtw8ioi3mB7YfZJuP"
);

export const initProject = async (payer) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createInitializeTx(payer.publicKey, program);
  const { blockhash } = await solConnection.getRecentBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  payer.signTransaction(tx);
  let txId = await solConnection.sendTransaction(tx, [payer.payer]);
  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("txHash =", txId);
};

export const initSellData = async (payer, mint) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createInitSellDataTx(mint, payer.publicKey, program);
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  const txId = await solConnection.sendRawTransaction(serializedTx, {
    skipPreflight: true,
    maxRetries: 2,
  });

  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const initUserPool = async (payer) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createInitUserTx(payer.publicKey, program);
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  const txId = await solConnection.sendRawTransaction(serializedTx, {
    skipPreflight: true,
    maxRetries: 2,
  });

  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const initAuctionData = async (payer, mint) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createInitAuctionDataTx(mint, payer.publicKey, program);
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  const txId = await solConnection.sendRawTransaction(serializedTx, {
    skipPreflight: true,
    maxRetries: 2,
  });

  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const updateFee = async (payer, solFee) => {
  console.log(solFee);
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createUpdateFeeTx(payer.publicKey, program, solFee);
  const { blockhash } = await solConnection.getRecentBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  payer.signTransaction(tx);
  let txId = await solConnection.sendTransaction(tx, [payer.payer]);
  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const addTreasury = async (payer, treasury, rate) => {
  console.log(treasury.toBase58(), rate);
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createAddTreasuryTx(
    payer.publicKey,
    treasury,
    rate,
    program
  );
  const { blockhash } = await solConnection.getRecentBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  payer.signTransaction(tx);
  let txId = await solConnection.sendTransaction(tx, [payer.payer]);
  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const removeTreasury = async (payer, treasury) => {
  console.log(treasury.toBase58());
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createRemoveTreasuryTx(payer.publicKey, program, treasury);
  const { blockhash } = await solConnection.getRecentBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  payer.signTransaction(tx);
  let txId = await solConnection.sendTransaction(tx, [payer.payer]);
  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const depositEscrow = async (payer, sol) => {
  let userAddress = payer.publicKey;
  console.log(userAddress.toBase58(), sol);

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    return;
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createDepositTx(userAddress, sol, program);
  const { blockhash } = await solConnection.getRecentBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  payer.signTransaction(tx);
  let txId = await solConnection.sendTransaction(tx, [payer.payer]);
  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const withdrawEscrow = async (payer, sol) => {
  let userAddress = payer.publicKey;
  console.log(userAddress.toBase58(), sol);

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    return;
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createWithdrawTx(userAddress, sol, program);
  const { blockhash } = await solConnection.getRecentBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  payer.signTransaction(tx);
  let txId = await solConnection.sendTransaction(tx, [payer.payer]);
  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const transfer = async (payer, mint, recipient) => {
  console.log(mint.toBase58(), recipient.toBase58());

  const [sellData, _] = await PublicKey.findProgramAddress(
    [Buffer.from(SELL_DATA_SEED), mint.toBuffer()],
    MARKETPLACE_PROGRAM_ID
  );
  console.log("Sell Data PDA: ", sellData.toBase58());

  let fromVault = 1;
  let poolAccount = await solConnection.getAccountInfo(sellData);
  if (poolAccount === null || poolAccount.data === null) {
    fromVault = 0;
  } else {
    const data = await getNFTPoolInfo(mint);
    if (data.active != 1) fromVault = 0;
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  let tx;
  if (fromVault)
    tx = await createTransferFromVaultTx(
      mint,
      payer.publicKey,
      recipient,
      program,
      solConnection
    );
  else
    tx = await createTransferTx(
      mint,
      payer.publicKey,
      recipient,
      program,
      solConnection
    );

  const { blockhash } = await solConnection.getRecentBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  payer.signTransaction(tx);
  let txId = await solConnection.sendTransaction(tx, [payer.payer]);
  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const listNftForSale = async (payer, item, priceSol) => {
  const mintPubkey = new PublicKey(item.mintAddr);
  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    await initUserPool(payer);
  }

  const [sellData, _] = await PublicKey.findProgramAddress(
    [Buffer.from(SELL_DATA_SEED), mintPubkey.toBuffer()],
    MARKETPLACE_PROGRAM_ID
  );
  console.log("Sell Data PDA: ", sellData.toBase58());

  let poolAccount = await solConnection.getAccountInfo(sellData);
  if (poolAccount === null || poolAccount.data === null) {
    await initSellData(payer, mintPubkey);
  }
  console.log("Sell Data PDA: ", sellData.toBase58());

  const [auctionData] = await PublicKey.findProgramAddress(
    [Buffer.from(AUCTION_DATA_SEED), mintPubkey.toBuffer()],
    MARKETPLACE_PROGRAM_ID
  );
  console.log("Auction Data PDA: ", auctionData.toBase58());

  poolAccount = await solConnection.getAccountInfo(auctionData);
  if (poolAccount === null || poolAccount.data === null) {
    await initAuctionData(payer, mintPubkey);
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createListForSellNftTx(
    mintPubkey,
    payer.publicKey,
    program,
    solConnection,
    priceSol
  );

  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();
  console.log("serializedTx", serializedTx);
  const listData = [];
  listData.push({
    tokenId: item.tokenId,
    name: item.name,
    imgUrl: item.imgUrl,
    mintAddr: item.mintAddr,
    seller: payer?.publicKey.toBase58(),
    buyer: "",
    collectionAddr: item.collectionAddr,
    metaDataUrl: item.metaDataUrl,
    solPrice: priceSol,
    txType: 0,
    mode: "listed",
  });

  return { listData, transactions: serializedTx };

  // const txId = await solConnection.sendRawTransaction(serializedTx, {
  //   skipPreflight: true,
  //   maxRetries: 2,
  // });

  // await solConnection.confirmTransaction(txId, "confirmed");
  // console.log("Your transaction signature", txId);
};

export const delistNft = async (payer, item) => {
  const mintPubkey = new PublicKey(item.mintAddr);
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );

  const tx = await createDelistNftTx(
    mintPubkey,
    payer.publicKey,
    program,
    solConnection
  );
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  console.log("serializedTx", serializedTx);

  const delistData = [];
  delistData.push(item.mintAddr);
  console.log("delistData", delistData);

  return { delistData, transactions: serializedTx };
};

export const setPrice = async (payer, mint, newPrice) => {
  const mintPubkey = new PublicKey(mint);
  console.log("mintPubkey", mintPubkey);
  console.log("newPrice", newPrice);
  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    await initUserPool(payer);
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createSetPriceTx(
    mintPubkey,
    payer.publicKey,
    newPrice,
    program
  );
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  console.log("serializedTx", serializedTx);

  return { newPrice, transaction: serializedTx, mintAddr: mint };
};

export const purchase = async (payer, items) => {
  console.log("items", items);
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    await initUserPool(payer);
  }

  const globalPool = await getGlobalState(program);
  const mintAddrArray = items.map((item) => new PublicKey(item.mintAddr));
  const txs = [];
  console.log("mintAddrArray", mintAddrArray);

  for (let i = 0; i < items.length; i++) {
    const tx = await createPurchaseTx(
      new PublicKey(items[i].mintAddr),
      payer.publicKey,
      globalPool.teamTreasury.slice(0, globalPool.teamCount.toNumber()),
      program,
      solConnection
    );
    if (!tx) return null;
    tx.feePayer = payer.publicKey;
    txs.push(tx);
  }

  const blockhash = (await solConnection.getLatestBlockhash()).blockhash;
  const purchaseData = [];
  for (let i = 0; i < items.length; i++) {
    txs[i].recentBlockhash = blockhash;

    purchaseData.push({
      tokenId: items[i].tokenId,
      imgUrl: items[i].imgUrl,
      mintAddr: items[i].mintAddr,
      seller: items[i].seller,
      buyer: payer?.publicKey.toBase58(),
      collectionAddr: items[i].collectionAddr,
      metaDataUrl: items[i].metaDataUrl,
      solPrice: items[i].solPrice,
      txType: 4,
    });
  }

  let signedTxs = await payer.signAllTransactions(txs);
  let serializedTxs = signedTxs.map((tx) => tx.serialize());

  return {
    purchaseData,
    mintAddrArray,
    transaction: serializedTxs,
  };
};

export const initOfferData = async (payer, mint) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createInitOfferDataTx(mint, payer.publicKey, program);
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  const txId = await solConnection.sendRawTransaction(serializedTx, {
    skipPreflight: true,
    maxRetries: 2,
  });

  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const makeOffer = async (payer, item, price) => {
  const mintPubkey = new PublicKey(item.mintAddr);

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    await initUserPool(payer);
    return;
  }

  const [offerData, _] = await PublicKey.findProgramAddress(
    [
      Buffer.from(OFFER_DATA_SEED),
      mintPubkey.toBuffer(),
      payer.publicKey.toBuffer(),
    ],
    MARKETPLACE_PROGRAM_ID
  );
  console.log("Offer Data PDA: ", offerData.toBase58());

  let poolAccount = await solConnection.getAccountInfo(offerData);
  if (poolAccount === null || poolAccount.data === null) {
    await initOfferData(payer, mintPubkey);
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createMakeOfferTx(
    mintPubkey,
    payer.publicKey,
    price,
    program
  );
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  const offerItem = [];

  offerItem.push({
    offerPrice: price,
    collectionAddr: item.collectionAddr,
    mintAddr: item.mintAddr,
    seller: item.seller,
    buyer: payer.publicKey.toBase58(),
    offerType: 'offer',
    imgUrl: item.imgUrl,
    tokenId: item.tokenId,
    active: 1,
  });

  console.log("offerItem", offerItem);
  return { transaction: serializedTx, offerItem };
};

export const cancelOffer = async (payer, mint) => {
  const mintPubkey = new PublicKey(mint);
  console.log("mintPubkey", mintPubkey);

  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  console.log(mintPubkey.toBase58());

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    await initUserPool(payer);
    return;
  }

  const tx = await createCancelOfferTx(mintPubkey, payer.publicKey, program);
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  return {
    transaction: serializedTx,
    mintAddr: mint,
    buyer: payer.publicKey.toBase58(),
  };
};

export const acceptOffer = async (payer, mintAddr, buyAddr) => {
  const mint = new PublicKey(mintAddr);
  const buyer = new PublicKey(buyAddr);

  console.log(mint.toBase58(), buyer.toBase58());

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    await initUserPool(payer);
    return;
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const globalPool = await getGlobalState(program);

  const tx = await createAcceptOfferTx(
    mint,
    buyer,
    globalPool.teamTreasury.slice(0, globalPool.teamCount.toNumber()),
    program,
    solConnection
  );
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();


  return {
    mintAddr: mintAddr,
    buyer: buyAddr,
    seller: payer.publicKey.toBase58(),
    transaction: serializedTx,
  };
};

export const createAuction = async (
  payer,
  item,
  startPrice,
  minIncrease,
  duration,
  reserved
) => {
  const mintPubkey = new PublicKey(item.mintAddr);

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    await initUserPool(payer);
    return;
  }

  const [sellData, _] = await PublicKey.findProgramAddress(
    [Buffer.from(SELL_DATA_SEED), mintPubkey.toBuffer()],
    MARKETPLACE_PROGRAM_ID
  );
  console.log("Sell Data PDA: ", sellData.toBase58());

  let poolAccount = await solConnection.getAccountInfo(sellData);
  if (poolAccount === null || poolAccount.data === null) {
    await initSellData(payer, mintPubkey);
  }

  const [auctionData] = await PublicKey.findProgramAddress(
    [Buffer.from(AUCTION_DATA_SEED), mintPubkey.toBuffer()],
    MARKETPLACE_PROGRAM_ID
  );
  console.log("Auction Data PDA: ", auctionData.toBase58());

  poolAccount = await solConnection.getAccountInfo(auctionData);
  if (poolAccount === null || poolAccount.data === null) {
    await initAuctionData(payer, mintPubkey);
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createCreateAuctionTx(
    mintPubkey,
    payer.publicKey,
    startPrice,
    minIncrease,
    duration,
    reserved,
    program,
    solConnection
  );
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  const data = [];
  data.push({
    name: item.name,
    tokenId: item.tokenId,
    collectionAddr: item.collectionAddr,
    imgUrl: item.imgUrl,
    mintAddr: item.mintAddr,
    seller: item.seller,
    metaDataUrl: item.metaDataUrl,
    startPrice: startPrice,
    mintIncrease: minIncrease,
    duration: duration,
    reserved: false,
    mode: "auction",
  });

  return { transaction: serializedTx, data };
};

export const placeBid = async (payer, mint, price) => {
  console.log(mint.toBase58(), price);

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    return;
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createPlaceBidTx(mint, payer.publicKey, price, program);
  const { blockhash } = await solConnection.getRecentBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  payer.signTransaction(tx);
  let txId = await solConnection.sendTransaction(tx, [payer.payer]);
  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const claimAuction = async (payer, mint) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  console.log(mint.toBase58());

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    return;
  }

  const globalPool = await getGlobalState(program);

  const tx = await createClaimAuctionTx(
    mint,
    payer.publicKey,
    globalPool.teamTreasury.slice(0, globalPool.teamCount.toNumber()),
    program,
    solConnection
  );
  const { blockhash } = await solConnection.getRecentBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  payer.signTransaction(tx);
  let txId = await solConnection.sendTransaction(tx, [payer.payer]);
  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const updateReserve = async (payer, mint, newPrice) => {
  console.log(mint.toBase58(), newPrice);

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    await initUserPool(payer);
  }
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const tx = await createUpdateReserveTx(
    mint,
    payer.publicKey,
    newPrice,
    program
  );
  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  const txId = await solConnection.sendRawTransaction(serializedTx, {
    skipPreflight: true,
    maxRetries: 2,
  });

  await solConnection.confirmTransaction(txId, "confirmed");
  console.log("Your transaction signature", txId);
};

export const cancelAuction = async (payer, mintAddr) => {
  const mint = new PublicKey(mintAddr);
  console.log("mint", mintAddr)
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  console.log(mint.toBase58());

  if (!(await isInitializedUser(payer.publicKey, solConnection))) {
    console.log(
      "User PDA is not Initialized. Should Init User PDA for first usage"
    );
    return;
  }

  const tx = await createCancelAuctionTx(
    mint,
    payer.publicKey,
    program,
    solConnection
  );

  const { blockhash } = await solConnection.getLatestBlockhash("confirmed");
  tx.feePayer = payer.publicKey;
  tx.recentBlockhash = blockhash;
  const serializedTx = (await payer.signTransaction(tx)).serialize();

  return { transaction: serializedTx, mintAddr: mintAddr }

};

export const getNFTPoolInfo = async (mint) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const nftData = await getNFTPoolState(mint, program);
  return {
    mint: nftData.mint.toBase58(),
    seller: nftData.seller.toBase58(),
    collection: nftData.collection.toBase58(),
    priceSol: nftData.priceSol.toNumber(),
    listedDate: nftData.listedDate.toNumber(),
    active: nftData.active.toNumber(),
  };
};

export const getOfferDataInfo = async (mint, userAddress) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const offerData = await getOfferDataState(mint, userAddress, program);
  return {
    mint: offerData.mint.toBase58(),
    buyer: offerData.buyer.toBase58(),
    offerPrice: offerData.offerPrice.toNumber(),
    offerListingDate: offerData.offerListingDate.toNumber(),
    active: offerData.active.toNumber(),
  };
};

export const getAuctionDataInfo = async (mint) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const auctionData = await getAuctionDataState(mint, program);
  return {
    mint: auctionData.mint.toBase58(),
    creator: auctionData.creator.toBase58(),
    startPrice: auctionData.startPrice.toNumber(),
    minIncreaseAmount: auctionData.minIncreaseAmount.toNumber(),
    startDate: auctionData.startDate.toNumber(),
    lastBidder: auctionData.lastBidder.toBase58(),
    lastBidDate: auctionData.lastBidDate.toNumber(),
    highestBid: auctionData.highestBid.toNumber(),
    duration: auctionData.duration.toNumber(),
    status: auctionData.status.toNumber(),
  };
};

export const getUserPoolInfo = async (userAddress) => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const userData = await getUserPoolState(userAddress, program);
  return {
    address: userData.address.toBase58(),
    escrowSol: userData.escrowSolBalance.toNumber(),
    tradedVolume: userData.tradedVolume.toNumber(),
  };
};

export const getGlobalInfo = async () => {
  let cloneWindow = window;
  let provider = new anchor.AnchorProvider(
    solConnection,
    cloneWindow["solana"],
    anchor.AnchorProvider.defaultOptions()
  );
  const program = new anchor.Program(
    NutMarketplace,
    MARKETPLACE_PROGRAM_ID,
    provider
  );
  const globalPool = await getGlobalState(program);
  const result = {
    admin: globalPool.superAdmin.toBase58(),
    marketFeeSol: globalPool.marketFeeSol.toNumber(),
    teamCount: globalPool.teamCount.toNumber(),
    teamTreasury: globalPool.teamTreasury
      .slice(0, globalPool.teamCount.toNumber())
      .map((info) => info.toBase58()),
    treasuryRate: globalPool.treasuryRate
      .slice(0, globalPool.teamCount.toNumber())
      .map((info) => info.toNumber()),
  };

  return result;
};

export const getAllNFTs = async (rpc) => {
  return await getAllListedNFTs(solConnection, rpc);
};

export const getAllOffersForNFT = async (address, rpc) => {
  return await getAllOffersForListedNFT(address, solConnection, rpc);
};
export const getAllAuctions = async (rpc) => {
  return await getAllStartedAuctions(solConnection, rpc);
};
